import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api } from "../api/client";
import DataTable, { Column } from "../components/DataTable";
import { IconLoader } from "../components/Icons";

interface AuditActivity {
  id: string;
  action: string;
  target_type: string;
  target_id: string;
  tenant_id: string | null;
  created_at: string;
}

interface DashboardData {
  total_tenants: number;
  active_tenants: number;
  total_users: number;
  total_devices: number;
  recent_activity: AuditActivity[];
}

export default function Dashboard() {
  const { t } = useTranslation();
  const { data, isLoading, error } = useQuery<DashboardData>({
    queryKey: ["ops-dashboard"],
    queryFn: async () => {
      const res = await api.get<DashboardData>("/ops/dashboard");
      return res.data;
    },
  });

  const statCards = [
    { label: t("dashboard.totalTenants"), value: data?.total_tenants ?? 0 },
    { label: t("dashboard.activeTenants"), value: data?.active_tenants ?? 0 },
    { label: t("dashboard.totalUsers"), value: data?.total_users ?? 0 },
    { label: t("dashboard.totalDevices"), value: data?.total_devices ?? 0 },
  ];

  const columns: Column<AuditActivity>[] = [
    { key: "action", header: t("auditLogs.action"), render: (r) => r.action },
    { key: "target", header: t("auditLogs.targetType"), render: (r) => `${r.target_type} / ${r.target_id}` },
    { key: "tenant", header: t("auditLogs.tenantId"), render: (r) => r.tenant_id ?? "—" },
    { key: "time", header: "Čas", render: (r) => new Date(r.created_at).toLocaleString("cs-CZ") },
  ];

  if (isLoading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 80 }}>
        <IconLoader size={32} className="muted" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="card" style={{ textAlign: "center", padding: 32, color: "var(--muted)" }}>
        {t("dashboard.loadError")}
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1>{t("nav.dashboard")}</h1>
        <p>{t("dashboard.subtitle")}</p>
      </div>

      <div className="grid grid-4" style={{ marginBottom: 32 }}>
        {statCards.map((s) => (
          <div className="card" key={s.label}>
            <div style={{ fontSize: 28, fontWeight: 700 }}>{s.value}</div>
            <div className="text-secondary" style={{ fontSize: 13 }}>{s.label}</div>
          </div>
        ))}
      </div>

      <div className="card">
        <h3 style={{ marginBottom: 16 }}>{t("dashboard.recentActivity")}</h3>
        <DataTable rows={data.recent_activity ?? []} columns={columns} emptyMessage={t("dashboard.noData")} />
      </div>
    </div>
  );
}
